import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, List, LayoutGrid } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/services/api';
import { toast } from 'sonner';
import { useConfirm } from '@/hooks/useConfirm';

const RoomsConfig = () => {
  const [rooms, setRooms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [services, setServices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ location_id: '', name: '', capacity: 1, service_type: '' });
  const [viewMode, setViewMode] = useState('list');
  const { confirm, ConfirmDialog } = useConfirm();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [roomsRes, locsRes, servicesRes] = await Promise.all([
        api.getRooms(), 
        api.getLocations(),
        api.getServices()
      ]);
      setRooms(roomsRes.data);
      setLocations(locsRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      toast.error('Error al cargar salas');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar campos obligatorios
    if (!formData.location_id || !formData.name || !formData.capacity || !formData.service_type) {
      toast.error('Por favor completa todos los campos obligatorios (*)');
      return;
    }
    
    try {
      if (editingItem) {
        await api.updateRoom(editingItem.id, formData);
        toast.success('Sala actualizada');
      } else {
        await api.createRoom(formData);
        toast.success('Sala creada');
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Error al guardar sala');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm('Eliminar Sala', '¿Estás seguro de eliminar esta sala? Esta acción no se puede deshacer.');
    if (!confirmed) return;
    try {
      await api.deleteRoom(id);
      toast.success('Sala eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ location_id: locations[0]?.id || '', name: '', capacity: 1, service_type: '' });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Salas</h2>
        <div className="flex space-x-2">
          <div className="flex border border-[#C9A961]/30 rounded">
            <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('list')} className={viewMode === 'list' ? 'bg-[#C9A961]' : ''}>
              <List size={16} />
            </Button>
            <Button variant={viewMode === 'cards' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('cards')} className={viewMode === 'cards' ? 'bg-[#C9A961]' : ''}>
              <LayoutGrid size={16} />
            </Button>
          </div>
          <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-[#C9A961]">
            <Plus className="mr-2" size={18} /> Nueva
          </Button>
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className="border border-[#C9A961]/20 rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Local</TableHead>
                <TableHead>Capacidad</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rooms.map((room) => {
                const location = locations.find(l => l.id === room.location_id);
                return (
                  <TableRow key={room.id}>
                    <TableCell className="font-medium">{room.name}</TableCell>
                    <TableCell>{location?.name || '-'}</TableCell>
                    <TableCell>{room.capacity}</TableCell>
                    <TableCell>{services.find(s => s.id === room.service_type)?.name || room.service_type}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button size="sm" variant="outline" onClick={() => { setEditingItem(room); setFormData(room); setShowModal(true); }}>
                          <Edit size={16} />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDelete(room.id)}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {rooms.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-[#6B6B6B] py-8">No hay salas registradas</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {rooms.map((room) => {
            const location = locations.find(l => l.id === room.location_id);
            return (
              <Card key={room.id} className="p-4">
                <h3 className="font-semibold text-lg">{room.name}</h3>
                <p className="text-sm text-[#6B6B6B]">{location?.name || 'Local'}</p>
                <p className="text-sm text-[#6B6B6B]">Capacidad: {room.capacity}</p>
                <p className="text-sm text-[#6B6B6B]">{services.find(s => s.id === room.service_type)?.name || room.service_type}</p>
                <div className="flex space-x-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => { setEditingItem(room); setFormData(room); setShowModal(true); }}>
                    <Edit size={16} />
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDelete(room.id)}>
                    <Trash2 size={16} />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#C9A961]">{editingItem ? 'Editar' : 'Nueva'} Sala</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Local *</Label>
              <Select value={formData.location_id} onValueChange={(v) => setFormData({...formData, location_id: v})}>
                <SelectTrigger><SelectValue placeholder="Seleccionar local" /></SelectTrigger>
                <SelectContent>{locations.map(loc => <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Nombre *</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required placeholder="Ej: Sala Principal" /></div>
            <div><Label>Capacidad *</Label><Input type="number" min="1" value={formData.capacity} onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value)})} required /></div>
            <div>
              <Label>Tipo de Servicio *</Label>
              <Select value={formData.service_type} onValueChange={(v) => setFormData({...formData, service_type: v})}>
                <SelectTrigger><SelectValue placeholder="Seleccionar servicio" /></SelectTrigger>
                <SelectContent>
                  {services.map(service => <SelectItem key={service.id} value={service.id}>{service.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>Cancelar</Button>
              <Button type="submit" className="bg-[#C9A961]">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </div>
  );
};

export default RoomsConfig;
